import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CredentialsSection } from "@/components/settings/CredentialsSection";
import { TelemetrCredentialsSection } from "@/components/settings/TelemetrCredentialsSection";
import { ThrottleSection } from "@/components/settings/ThrottleSection";
import {
  DataSettingsCard,
  GeneralSettingsCard,
  ImportSettingsCard,
  InboxSettingsCard,
  InviterSettingsCard,
  NotificationsCard,
  ProxySettingsCard,
  SecuritySettingsCard,
  WarmupSettingsCard,
} from "@/components/settings/CompanySettingsSections";
import { PageHeader } from "@/components/ui/page-header";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { HelpSection } from "@/components/HelpSection";

type Tab =
  | "general"
  | "accounts"
  | "acquisition"
  | "notifications"
  | "credentials"
  | "integrations"
  | "throttle";

const TABS: Tab[] = [
  "general",
  "accounts",
  "acquisition",
  "notifications",
  "credentials",
  "integrations",
  "throttle",
];

function readHashTab(): Tab {
  const h = window.location.hash.replace(/^#/, "");
  return (TABS as string[]).includes(h) ? (h as Tab) : "general";
}

/**
 * SettingsPage — /settings. Hash-routed tabs (no router migration).
 *
 * Company-wide preferences (general / accounts / acquisition / notifications)
 * persist via /api/company/settings; the last three tabs (credentials,
 * integrations, throttle) are the pre-existing integration/secret sections.
 */
export function SettingsPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>(readHashTab);

  useEffect(() => {
    const sync = () => setTab(readHashTab());
    window.addEventListener("hashchange", sync);
    return () => window.removeEventListener("hashchange", sync);
  }, []);

  const selectTab = (next: string) => {
    const next_ = next as Tab;
    setTab(next_);
    window.history.replaceState(null, "", `#${next_}`);
  };

  return (
    <section className="mx-auto max-w-7xl px-6 py-8 space-y-6">
      <PageHeader title={t("settings.title")} subtitle={t("settings.subtitle")} />

      {/* Forms stay narrow for readability, but the page gutter matches every
          other page so the layout doesn't shift on navigation. */}
      <Tabs className="max-w-4xl" value={tab} onValueChange={selectTab}>
        <TabsList>
          {TABS.map((key) => (
            <TabsTrigger key={key} value={key}>
              {t(`settings.tab.${key}`)}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="general">
          <div className="space-y-6">
            <GeneralSettingsCard />
            <SecuritySettingsCard />
            <DataSettingsCard />
          </div>
        </TabsContent>

        <TabsContent value="accounts">
          <div className="space-y-6">
            <ImportSettingsCard />
            <WarmupSettingsCard />
            <ProxySettingsCard />
          </div>
        </TabsContent>

        <TabsContent value="acquisition">
          <div className="space-y-6">
            <InviterSettingsCard />
            <InboxSettingsCard />
          </div>
        </TabsContent>

        <TabsContent value="notifications">
          <NotificationsCard />
        </TabsContent>

        <TabsContent value="credentials">
          <CredentialsSection />
        </TabsContent>

        <TabsContent value="integrations">
          <TelemetrCredentialsSection />
        </TabsContent>

        <TabsContent value="throttle">
          <ThrottleSection />
        </TabsContent>
      </Tabs>

      <div className="max-w-4xl">
        <HelpSection i18nKey="settings.help" paragraphs={2} />
      </div>
    </section>
  );
}
